import { FC, useCallback } from 'react';
import { Input, RadioGroup, RadioGroupItem } from '@features/ui';
import {
  Control,
  Controller,
  ControllerRenderProps,
  UseFormGetValues,
  UseFormReset,
  useWatch,
} from 'react-hook-form';
import { zipCodeData } from '@features/admin/feeSettings/feeSettingData.ts';
import {
  DomesticSalesFeeFormData,
  ZipCodeType,
} from '@src/types/feeSettings.ts';

interface ZipCodeFormProps {
  control: Control<DomesticSalesFeeFormData>;
  reset: UseFormReset<DomesticSalesFeeFormData>;
  getValues: UseFormGetValues<DomesticSalesFeeFormData>;
}

export const ZipCodeForm: FC<ZipCodeFormProps> = ({
  control,
  reset,
  getValues,
}) => {
  const type = useWatch({ control, name: 'zip_code' });

  const renderZipCodeTypeContent = useCallback(() => {
    return (
      <>
        {/* Specific */}
        <div
          className={'mt-10 lg:max-w-[500px] w-full'}
          style={{
            display: type === ZipCodeType.Specific ? 'block' : 'none',
          }}
        >
          <Controller
            control={control}
            name="specific"
            render={({ field }) => (
              <div className={'flex flex-col gap-2'}>
                <Input
                  placeholder={'Enter'}
                  textSize={'xs'}
                  type={'number'}
                  variant={'secondary'}
                  {...field}
                />
                <p>(Use commas to add multiple Zip code)</p>
              </div>
            )}
          />
        </div>

        {/* Range */}
        <div
          style={{
            display: type === ZipCodeType.Range ? 'block' : 'none',
          }}
        >
          <div className={'flex mt-10 w-full gap-6 items-center'}>
            <Controller
              control={control}
              name="range_from"
              render={({ field }) => (
                <div>
                  <Input
                    placeholder={'Enter'}
                    textSize={'xs'}
                    type={'number'}
                    variant={'secondary'}
                    {...field}
                  />
                </div>
              )}
            />
            <p>to</p>
            <Controller
              control={control}
              name="range_to"
              render={({ field }) => (
                <div>
                  <Input
                    placeholder={'Enter'}
                    textSize={'xs'}
                    type={'number'}
                    variant={'secondary'}
                    {...field}
                  />
                </div>
              )}
            />
          </div>
          <p className={'mt-2'}>eg 95131 to 95136</p>
        </div>
      </>
    );
  }, [type, control]);

  const handleChangeZipCodeType = useCallback(
    (
      field: ControllerRenderProps<DomesticSalesFeeFormData, 'zip_code'>,
      val: ZipCodeType
    ) => {
      const currentValue = getValues();

      reset({
        ...currentValue,
        specific: undefined,
        range_from: undefined,
        range_to: undefined,
      });
      field.onChange(val);
    },
    [getValues, reset]
  );

  return (
    <>
      <Controller
        control={control}
        name="zip_code"
        render={({ field }) => (
          <div className={'flex flex-1 gap-7 lg:max-w-[500px] w-full'}>
            <p>Zip Code</p>
            <RadioGroup
              className={'gap-x-6 flex'}
              onValueChange={(value) =>
                handleChangeZipCodeType(field, value as ZipCodeType)
              }
            >
              {zipCodeData.map((item) => {
                return (
                  <div
                    key={item.value}
                    className={'flex items-center gap-x-2.5 '}
                  >
                    <RadioGroupItem value={item.value} />
                    <p>{item.label}</p>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        )}
      />
      {renderZipCodeTypeContent()}
    </>
  );
};
